import React, { useCallback } from 'react';
import DatePicker from 'react-multi-date-picker';
import { useField } from 'formik';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import islamic from 'react-date-object/calendars/arabic';
import islamic_ar from 'react-date-object/locales/arabic_ar';
import islamic_en from 'react-date-object/locales/arabic_en';

const validateHijriDate = (date, t) => {
  try {
    if (!date || typeof date !== 'object') {
      return t("Invalid date");
    }

    const year = date?.year;
    const month = date?.month?.number ?? date?.month;
    const day = date?.day;

    if (!year || !month || !day) {
      return t("Incomplete date");
    }

    if (month < 1 || month > 12) {
      return t("Invalid Hijri Month (1-12)");
    }

    const maxDays = date?.month?.length || 30;

    if (day < 1 || day > maxDays) {
      return `${t("Invalid Hijri day")} (Max ${maxDays})`;
    }

    const jsDate = date?.toDate?.();
    if (!jsDate || isNaN(jsDate.getTime())) {
      return t("Invalid date conversion");
    }

    const minDate = moment().subtract(150, "years").toDate();
    if (jsDate < minDate) {
      return t("Age cannot be more than 150 years");
    }

    if (jsDate > new Date()) {
      return t("Future dates are not allowed");
    }

    return null;
  } catch {
    return t("Invalid date");
  }
};

const validateRawInput = (inputValue, t) => {
  if (!inputValue) return null;

  const parts = inputValue.split(/[\/\-]/);

  if (parts.length !== 3) {
    return t("Invalid date format (DD/MM/YYYY)");
  }

  let [day, month, year] = parts.map(Number);

  if (!day || !month || !year) {
    return t("Invalid date");
  }

  if (month < 1 || month > 12) {
    return t("Invalid Hijri Month (1-12)");
  }

  if (day < 1 || day > 30) {
    return t("Invalid Hijri day (1-30)");
  }

  return null;
};

const FormikHijriDatePicker = ({
  name,
  label = '',
  placeholder = 'اختر التاريخ',
  className = '',
  locale = 'ar',
  format = 'DD/MM/YYYY',
  ...props
}) => {
  const { t } = useTranslation();
  const [field, meta, helpers] = useField(name);

  const handleChange = useCallback((date, { inputValue } = {}) => {
    helpers.setTouched(true);

    const rawError = validateRawInput(inputValue, t);
    if (rawError) {
      helpers.setError(rawError);
      helpers.setValue(null);
      return;
    }

    if (!date) {
      helpers.setValue(null);
      helpers.setError(undefined);
      return;
    }

    const error = validateHijriDate(date, t);

    if (error) {
      helpers.setError(error);
      helpers.setValue(null);
      return;
    }

    helpers.setError(undefined);
    helpers.setValue(date.toDate());
  }, [helpers, t]);

  return (
    <div className={`flex flex-col gap-1 w-full mb-1 ${className}`}>
      {label && <label htmlFor={name} className="input-label">{label}</label>}

      <DatePicker
        id={name}
        value={field.value}
        onChange={handleChange}
        onBlur={() => helpers.setTouched(true)}
        calendar={islamic}
        locale={locale === "ar" ? islamic_ar : islamic_en}
        format={format}
        placeholder={placeholder}
        containerClassName="w-full"
        inputClass="input-field !text-[12px] w-full"
        maxDate={new Date()}
        minDate={moment().subtract(150, "years").toDate()}
        editable={true}
        {...props}
      />

      {meta.error && meta.touched && (
        <p className="text-sm text-red-500">{meta.error}</p>
      )}
    </div>
  );
};

const StandaloneHijriDatePicker = ({
  label = '',
  placeholder = 'اختر التاريخ',
  className = '',
  value = null,
  handleChange = () => { },
  locale = 'ar',
  format = 'DD-MM-YYYY',
  ...props
}) => {
  const { t } = useTranslation();

  const safeChange = (date, { inputValue } = {}) => {
    const rawError = validateRawInput(inputValue, t);
    if (rawError) {
      handleChange(null);
      return;
    }

    const error = validateHijriDate(date, t);
    if (error) {
      handleChange(null);
      return;
    }

    handleChange(date?.toDate?.() || null);
  };

  return (
    <div className={`flex flex-col gap-1 w-full mb-1 ${className}`}>
      {label && (
        <label htmlFor="standalone-hijri-picker" className="input-label">
          {label}
        </label>
      )}

      <DatePicker
        id="standalone-hijri-picker"
        value={value}
        onChange={safeChange}
        calendar={islamic}
        locale={locale === 'ar' ? islamic_ar : islamic_en}
        format={format}
        placeholder={placeholder}
        containerClassName="w-full"
        inputClass="input-field !text-[12px] w-full"
        maxDate={new Date()}
        minDate={moment().subtract(150, "years").toDate()}
        editable={true}
        {...props}
      />
    </div>
  );
};

const HijriDatePicker = (props) => {
  const { useFormik = true } = props;
  return useFormik
    ? <FormikHijriDatePicker {...props} />
    : <StandaloneHijriDatePicker {...props} />;
};

export default HijriDatePicker;